import { RDirectory, RLicense, RRole, RUser } from '@counsel-project/counsel-auth-api'
import { isApiError } from '@counsel-project/client-utils'
import { StateCreator } from 'zustand'
import { authRequest } from '../../api/auth-api'
import checkToken from '../../auth/checkToken'
import log from '../../logging'

export type AuthStoreValues = {
  user: RUser | null
  /** From `licenses.getPrimary`, the same license and organization the backends resolve from. */
  primaryLicense: RLicense | null
  /** The role `getPrimary` attributed to {@link primaryLicense}, carrying the viewer's seats. */
  primaryRole: RRole | null
  directory: RDirectory | null
  setDirectory: (directory: RDirectory | null) => void
  directories: RDirectory[]
  setDirectories: (directories: RDirectory[]) => void
  directoryLicense: RLicense | null
  setDirectoryLicense: (license: RLicense | null) => void
}

export type AuthStore = AuthStoreValues & {
  setUser: (user: RUser | null) => void
  populatePrimaryLicense: () => Promise<void>
  setPrimaryLicense: (license: RLicense | null, role?: RRole | null) => void
}

const createAuthSlice: StateCreator<AuthStore> = (set, get) => ({
  user: null,
  setUser: (user: RUser | null) => set({ user }),
  primaryLicense: null,
  primaryRole: null,
  setPrimaryLicense: (license: RLicense | null, role: RRole | null = null) =>
    set({ primaryLicense: license, primaryRole: role }),
  populatePrimaryLicense: async () => {
    try {
      const loggedIn = await checkToken(true)
      if (!loggedIn) return

      const res = await authRequest.user.licenses.getPrimary({ token: '' })
      set({ primaryLicense: res.license, primaryRole: res.role ?? null })
    } catch (err) {
      log.error(err)
      if (isApiError(err)) {
        if (err.msg === 'No active license') {
          set({ primaryLicense: null, primaryRole: null })
        }
      }
    }
  },
  directory: null,
  setDirectory: (directory: RDirectory | null) => set({ directory }),
  directories: [],
  setDirectories: (directories: RDirectory[]) => set({ directories }),
  directoryLicense: null,
  setDirectoryLicense: (license: RLicense | null) => set({ directoryLicense: license }),
})

export default createAuthSlice
